import * as React from "react";
import AppIcon from "../components/AppIcon";
import { useUser } from "../utils/AccountStore";
import { useLocation, useNavigate } from "react-router-dom";
import { GetMetadata, Metadata } from "../utils/MetadataStore";
import { Scope, createScope, animate, createSpring, svg } from "animejs";
import TailwindMenuItem from "../components/TailwindMenuItem";
import FloatingVideoWindow from "../components/FloatingVideoWindow";
import { videos } from "./Videos";
import { FaTicketAlt } from "react-icons/fa";

const Home = () => {
    const logoMaxSize = typeof window !== "undefined" && window.screen?.width > 600 ? "400px" : "100%";

    const location = useLocation();
    const { user } = useUser();

    const navigate = useNavigate();

    const [metadata, setMetadata] = React.useState({} as Metadata);
    const [isLoading, setIsLoading] = React.useState(true);
    const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
    const [showInstallPrompt, setShowInstallPrompt] = React.useState(false);
    const [isInstallBannerVisible, setIsInstallBannerVisible] = React.useState(true);
    const [canInstall, setCanInstall] = React.useState(false);
    const [installIconError, setInstallIconError] = React.useState(false);

    React.useEffect(() => {
        if (user) {
            setIsLoading(true);
            GetMetadata().then((res) => {
                setMetadata(res);
                setIsLoading(false);
            });
        }
    }, [user]);

    // Handle PWA install prompt
    React.useEffect(() => {
        const handler = (e: Event) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later
            setDeferredPrompt(e);
            setCanInstall(true);

            // Get the number of times the prompt has been shown
            const shownCount = parseInt(localStorage.getItem("installPromptShownCount") || "0");

            // Only show popup if it's been shown less than 3 times
            if (shownCount >= 3) {
                setShowInstallPrompt(false);
                return;
            }

            // Check if user previously dismissed recently (don't show again for 3 days)
            const dismissed = localStorage.getItem("installPromptDismissed");
            if (dismissed) {
                const dismissedTime = parseInt(dismissed);
                const daysSinceDismissal = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
                if (daysSinceDismissal < 3) {
                    setShowInstallPrompt(false);
                    return;
                }
            }

            // Increment the shown count
            localStorage.setItem("installPromptShownCount", (shownCount + 1).toString());

            // Delay showing the prompt slightly for better UX
            setTimeout(() => {
                setShowInstallPrompt(true);
            }, 2000);
        };

        window.addEventListener("beforeinstallprompt", handler);

        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            return;
        }

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;

        console.log(`User response to install prompt: ${outcome}`);

        // Clear the deferredPrompt so it can be garbage collected
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
        setCanInstall(false);

        // Store that user took action
        localStorage.setItem("installPromptActioned", Date.now().toString());

        // Reset the count since user installed or saw the native prompt
        if (outcome === "accepted") {
            localStorage.setItem("installPromptShownCount", "999"); // Don't show again
        }
    };

    const handleDismissInstall = () => {
        setIsInstallBannerVisible(false);
        setTimeout(() => {
            setShowInstallPrompt(false);
        }, 300); // Wait for animation to complete

        // Store dismissal in localStorage to avoid showing it again for 3 days
        localStorage.setItem("installPromptDismissed", Date.now().toString());
    };

    const root = React.useRef(null);
    const scope = React.useRef<Scope | null>(null);

    React.useEffect(() => {
        if (scope && root.current) {
            scope.current = createScope({ root: root.current }).add((self) => {
                // Add subtle floating animation to the logo
                animate(".logo", {
                    translateY: [
                        { to: -10, ease: "inOut(2)", duration: 2000 },
                        { to: 0, ease: "inOut(2)", duration: 2000 },
                    ],
                    loop: true,
                });

                // Add entrance animation for menu items
                animate(".menu-item", {
                    opacity: [0, 1],
                    translateY: [20, 0],
                    delay: (el, i) => i * 100,
                    duration: 600,
                    ease: "outBack(1.5)",
                });
            });
        }

        return () => scope?.current?.revert();
    }, [metadata.eventLogo]); // Re-run when logo loads

    return (
        <div
            ref={root}
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            {user ? (
                <div className="flex flex-col min-h-screen" style={{ backgroundColor: "rgba(255,255,255,0.5)" }}>
                    {/* Floating Video Window */}
                    <FloatingVideoWindow videos={videos} />

                    {/* Install App Button - Top Right */}
                    {canInstall && (
                        <button
                            onClick={handleInstallClick}
                            className="fixed top-4 right-4 z-50 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 group"
                            style={{
                                backgroundColor: "#DB1C7D",
                                animation: "slideInFromRight 0.5s ease-out",
                            }}
                            title="Install The Collective App"
                        >
                            <svg className="w-6 h-6 text-white transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            {/* Tooltip */}
                            <span
                                className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                                style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                            >
                                Install App
                                <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900" style={{ marginLeft: "-1px" }}></span>
                            </span>
                        </button>
                    )}

                    {/* Small Mobile-Friendly Install Popup - Bottom of Screen */}
                    {showInstallPrompt && isInstallBannerVisible && (
                        <div
                            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 transition-all duration-300"
                            style={{
                                transform: isInstallBannerVisible ? "translateY(0)" : "translateY(120%)",
                                opacity: isInstallBannerVisible ? 1 : 0,
                            }}
                        >
                            <div
                                className="bg-white rounded-2xl shadow-2xl p-4 border-2 backdrop-blur-sm"
                                style={{
                                    borderColor: "#DB1C7D",
                                    boxShadow: "0 10px 40px rgba(219, 28, 125, 0.3)",
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    {/* App Icon */}
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#DB1C7D" }}>
                                            {installIconError ? (
                                                <AppIcon kind="mobile" className="text-2xl text-white" />
                                            ) : (
                                                <img
                                                    src="/images/thecollectivemarket-pink.png"
                                                    alt="App Icon"
                                                    className="w-10 h-10 object-contain"
                                                    onError={() => setInstallIconError(true)}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-bold text-sm mb-0.5" style={{ color: "#013A51" }}>
                                            Install The Collective
                                        </h4>
                                        <p className="text-xs" style={{ color: "#708372" }}>
                                            Quick access from your home screen
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleInstallClick}
                                            className="px-4 py-2 rounded-lg font-semibold text-sm text-white transition-all duration-200 hover:shadow-lg transform hover:scale-105 active:scale-95"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        >
                                            Install
                                        </button>
                                        <button onClick={handleDismissInstall} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Dismiss">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Small indicator for mobile users */}
                                <div className="flex items-center justify-center mt-2 md:hidden">
                                    <div className="w-8 h-1 rounded-full" style={{ backgroundColor: "rgba(219, 28, 125, 0.3)" }}></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Banner/Hero Section */}
                    <div className="relative flex justify-center items-center min-h-[40vh]">
                        <div className="text-center space-y-4 max-w-2xl mx-auto px-8">
                            <div className="animate-fade-in transform hover:scale-105 transition-transform duration-300">
                                {isLoading ? (
                                    <div className="mx-auto bg-gray-300 rounded-lg animate-pulse" style={{ width: logoMaxSize, height: "150px" }}></div>
                                ) : (
                                    <img
                                        className="logo mx-auto object-contain drop-shadow-xl"
                                        src={"/images/thecollectivemarket-pink.png"}
                                        style={{ width: logoMaxSize, maxHeight: "200px" }}
                                        alt="The Collective Market Logo"
                                    />
                                )}
                            </div>

                            {/* Banner Content */}
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-4 shadow-lg" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    Welcome to The Collective
                                </h1>
                                <p className="text-sm md:text-base" style={{ color: "#708372" }}>
                                    Artisan Markets - Discover unique crafts, artisanal goods, and local treasures
                                </p>
                            </div>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-10 left-10 w-20 h-20 rounded-full opacity-20 animate-pulse" style={{ backgroundColor: "#DB1C7D" }}></div>
                        <div className="absolute right-10 w-16 h-16 rounded-full opacity-20 animate-pulse delay-1000" style={{ backgroundColor: "#DDB649" }}></div>
                    </div>

                    {/* Navigation Sections */}
                    <div className="flex-1 py-12">
                        <div className="max-w-6xl mx-auto px-8">
                            {/* Main Navigation Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                                {/* About Us Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                                        onClick={() => navigate("/about")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #708372 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl">ℹ️</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">About Us</h3>
                                            <p className="text-white text-opacity-90 text-sm">Learn about our market</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Vendors Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(112, 131, 114, 0.9)" }}
                                        onClick={() => navigate("/exhibitors")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DDB649 0%, #8B6F4C 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl"><AppIcon kind="store" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">Vendors</h3>
                                            <p className="text-white text-opacity-90 text-sm">Meet our artisans</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Map Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(1, 58, 81, 0.9)" }}
                                        onClick={() => navigate("/floorPlans")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #7999BA 0%, #DDB649 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl"><AppIcon kind="map" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">Map</h3>
                                            <p className="text-white text-opacity-90 text-sm">Navigate the market</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Contact Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(139, 111, 76, 0.9)" }}
                                        onClick={() => navigate("/contact")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #7999BA 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl"><AppIcon kind="phone" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">Contact</h3>
                                            <p className="text-white text-opacity-90 text-sm">Get in touch</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* QR Section */}
                                {/* <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(221, 182, 73, 0.9)" }}
                                        onClick={() => navigate("/scanner")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #013A51 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl"><AppIcon kind="qr" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold" style={{ color: "#013A51" }}>
                                                QR Scanner
                                            </h3>
                                            <p className="text-opacity-90 text-sm" style={{ color: "#013A51" }}>
                                                Scan vendor codes
                                            </p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div> */}

                                {/* Tickets Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                        onClick={() => navigate("/tickets")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #013A51 0%, #708372 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <FaTicketAlt aria-hidden="true" className="text-2xl" style={{ color: "#013A51" }} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">My Tickets</h3>
                                            <p className="text-white text-opacity-90 text-sm">View your event tickets</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DDB649" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Sponsors Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}
                                        onClick={() => navigate("/sponsors")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #7999BA 0%, #708372 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}>
                                                <span className="text-2xl"><AppIcon kind="handshake" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold" style={{ color: "#013A51" }}>
                                                Our Partners
                                            </h3>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Entertainment Section */}
                                <div className="menu-item group">
                                    <div
                                        className="relative h-48 rounded-2xl p-6 flex flex-col justify-center items-center cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden backdrop-blur-sm"
                                        style={{ backgroundColor: "rgba(221, 182, 73, 0.9)" }}
                                        onClick={() => navigate("/entertainment")}
                                    >
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #013A51 100%)" }}
                                        ></div>
                                        <div className="relative z-10 text-center">
                                            <div className="mb-4 p-4 rounded-full mx-auto w-16 h-16 flex items-center justify-center" style={{ backgroundColor: "#F1F5F8" }}>
                                                <span className="text-2xl"><AppIcon kind="theater" /></span>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">Entertainment</h3>
                                            <p className="text-white text-opacity-90 text-sm">See what's on</p>
                                        </div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                            style={{ backgroundColor: "#DB1C7D" }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: "#DB1C7D" }}></div>
                        <p style={{ color: "#013A51" }}>Loading...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;

// Add keyframe animation styles
const style = document.createElement("style");
style.textContent = `
    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(100%);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes slideInFromRight {
        from {
            opacity: 0;
            transform: translateX(100px) scale(0.8);
        }
        to {
            opacity: 1;
            transform: translateX(0) scale(1);
        }
    }
    
    /* Add safe area padding for mobile devices with notches */
    @supports (padding-bottom: env(safe-area-inset-bottom)) {
        .fixed.bottom-4 {
            padding-bottom: env(safe-area-inset-bottom);
        }
    }
    
    /* Ensure install button doesn't interfere with top elements */
    @supports (padding-top: env(safe-area-inset-top)) {
        .fixed.top-4 {
            padding-top: env(safe-area-inset-top);
        }
    }
`;
if (typeof document !== "undefined" && !document.getElementById("home-animations")) {
    style.id = "home-animations";
    document.head.appendChild(style);
}
